import { MouseEvent, ReactNode, useMemo } from 'react';
import type { EmailTemplate } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/email-template/v1/emailtemplate.pb';
import { EmailTemplateQueries } from '@frontend/api-email-template';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { BulkEmailPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import {
  ListRow,
  Modal,
  NakedButton,
  PopoverMenu,
  Text,
  useModalControl,
  usePopoverMenu,
} from '@frontend/design-system';

type Action = {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  destructive?: boolean;
};

export type TemplateCardActionsProps = {
  template: EmailTemplate;
  onDelete?: (templateId: string) => void;
  onDuplicate?: (template: EmailTemplate) => void;
  onSelect: () => void;
  onView?: (template: EmailTemplate) => void;
};

export const TemplateCardActions = ({
  template,
  onDelete,
  onDuplicate,
  onSelect,
  onView,
}: TemplateCardActionsProps) => {
  const { t } = useTranslation('bulk-messaging');
  const { getTriggerProps, getMenuProps, close, open } = usePopoverMenu({
    placement: 'bottom-end',
  });
  const { modalProps: deleteModalProps, openModal: openDeleteModal } = useModalControl();

  const handleConfirmDelete = () => {
    if (template.id) onDelete?.(template.id);
    deleteModalProps.onClose();
  };

  const actions: Action[] = useMemo(() => {
    const rows: Action[] = [
      {
        icon: <Icon name='view' size={20} color='subdued' />,
        label: t('View'),
        onClick: () => {
          close();
          onView?.(template);
        },
      },
      {
        icon: <Icon name='arrow-right-small' size={20} color='subdued' />,
        label: t('Select This Template'),
        onClick: () => {
          close();
          onSelect();
        },
      },
    ];

    const isSystem = EmailTemplateQueries.isSystemEmailTemplate(template);

    if (!isSystem) {
      rows.push({
        icon: <Icon name='copy' size={20} color='subdued' />,
        label: t('Duplicate'),
        onClick: () => {
          close();
          onDuplicate?.(template);
        },
      });
      rows.push({
        icon: <Icon name='trash' size={20} css={{ color: theme.colors.critical50 }} />,
        label: t('Delete Template'),
        onClick: () => {
          close();
          openDeleteModal();
        },
        destructive: true,
      });
    }

    return rows;
  }, [t, template, onDuplicate, onSelect, onView, close, openDeleteModal]);

  return (
    <>
      <NakedButton
        {...getTriggerProps()}
        css={templateCardMoreButtonStyle}
        onClick={(e: MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          open();
        }}
        trackingId={`${BulkEmailPrefixes.Templates}-compose-card-menu-btn`}
      >
        <Icon name='more' size={20} />
      </NakedButton>
      <PopoverMenu {...getMenuProps()}>
        {actions.map(({ icon, label, onClick, destructive }, index) => (
          <ListRow
            key={label}
            css={{
              border: 'unset',
              display: 'flex',
              columnGap: theme.spacing(1),
              padding: theme.spacing(1.25, 2),
              cursor: 'pointer',
              borderBottom: `1px solid ${theme.colors.neutral20}`,
              '&:first-of-type': {
                paddingTop: theme.spacing(1),
              },
              '&:last-of-type': {
                borderBottom: 'none',
                paddingBottom: theme.spacing(1),
              },
            }}
            onClick={(e: MouseEvent<HTMLLIElement>) => {
              e.stopPropagation();
              onClick();
            }}
            data-trackingid={`${BulkEmailPrefixes.Templates}-compose-card-action-${index}`}
          >
            {icon}
            <Text css={destructive ? { color: theme.colors.critical50 } : undefined}>{label}</Text>
          </ListRow>
        ))}
      </PopoverMenu>
      {!EmailTemplateQueries.isSystemEmailTemplate(template) && (
        <Modal {...deleteModalProps} minWidth={600}>
          <Modal.Header onClose={deleteModalProps.onClose}>{t('Are You Sure?')}</Modal.Header>
          <Modal.Body>
            <Text>{t('Are you sure you want to delete this template? This action cannot be undone.')}</Text>
          </Modal.Body>
          <Modal.Actions
            secondaryLabel={t('Cancel')}
            primaryLabel={t('Delete Template')}
            onSecondaryClick={deleteModalProps.onClose}
            onPrimaryClick={handleConfirmDelete}
            destructive
          />
        </Modal>
      )}
    </>
  );
};

const templateCardMoreButtonStyle = {
  flexShrink: 0,
  padding: theme.spacing(0.5),
  borderRadius: theme.borderRadius.small,
  '&:hover': {
    backgroundColor: theme.colors.neutral5,
  },
};
